import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { SEARCE_COLORS } from '../../theme';

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

export type MarqueeBannerProps = {
  text: string;
  /** Page marquees use primary tint; global uses a distinct amber tint. */
  variant?: 'page' | 'global';
};

/**
 * Scrolling announcement banner (shared by page-specific and global marquees).
 */
export function MarqueeBanner({ text, variant = 'page' }: MarqueeBannerProps) {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const isGlobal = variant === 'global';
  const accent = isGlobal ? SEARCE_COLORS.warning : SEARCE_COLORS.primary;

  return (
    <Box
      role="marquee"
      aria-live="polite"
      sx={{
        mb: 1.5,
        py: 0.85,
        px: 2,
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: alpha(accent, isGlobal ? 0.14 : 0.1),
        border: `1px solid ${alpha(accent, isGlobal ? 0.45 : 0.35)}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: 'max-content',
          animation: `${scroll} 24s linear infinite`,
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            width: '100%',
          },
        }}
      >
        {[0, 1].map((i) => (
          <Typography
            key={i}
            component="span"
            aria-hidden={i === 1 ? true : undefined}
            sx={{
              flexShrink: 0,
              pr: 6,
              color: isGlobal ? SEARCE_COLORS.warningDark ?? accent : accent,
              fontWeight: 600,
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              '@media (prefers-reduced-motion: reduce)': {
                pr: 0,
                whiteSpace: 'normal',
              },
            }}
          >
            {trimmed}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

export default MarqueeBanner;
